import { useCallback, useRef, useState } from 'react'
import type { BackgroundsSizeFilter } from '@/lib/backgrounds/types/backgrounds-size-filter'
import type { BackgroundsTypeOption } from '@/lib/backgrounds/types/backgrounds-type-option'
import {
  type BackgroundsPanelSettings,
  type BackgroundsPlacedFilter,
  DEFAULT_BACKGROUNDS_PANEL_SETTINGS,
} from '@/lib/backgrounds/backgrounds-panel-settings'

/** What "Clear filters" resets — Type, Show filename and sort are settings, not filters. */
const CLEARED_FILTERS: Partial<BackgroundsPanelSettings> = {
  keywordIds: [],
  filenameQuery: '',
  size: 'any',
  placed: 'any',
}

/**
 * The Backgrounds panel's settings (Type, filters, Show filename, sort, Filters expanded):
 * controlled by the host when `controlledSettings` is passed, otherwise kept here. Returns the
 * current settings plus one setter per field, Clear filters and the Type change.
 */
export function useBackgroundsPanelSettings(
  controlledSettings: BackgroundsPanelSettings | undefined,
  onSettingsChange: ((settings: BackgroundsPanelSettings) => void) | undefined
) {
  const [internalSettings, setInternalSettings] = useState(DEFAULT_BACKGROUNDS_PANEL_SETTINGS)
  const settings = controlledSettings ?? internalSettings

  // Patches merge onto the latest settings (not this render's), so two updates in one event —
  // e.g. a Type change that also clears filters — can't overwrite each other.
  const settingsRef = useRef(settings)

  settingsRef.current = settings

  const updateSettings = useCallback(
    (patch: Partial<BackgroundsPanelSettings>) => {
      const next = { ...settingsRef.current, ...patch }

      settingsRef.current = next
      if (onSettingsChange) onSettingsChange(next)
      else setInternalSettings(next)
    },
    [onSettingsChange]
  )

  const setSelectedKeywordIds = useCallback(
    (keywordIds: string[]) => updateSettings({ keywordIds }),
    [updateSettings]
  )
  const setFilenameQuery = useCallback(
    (nextFilenameQuery: string) => updateSettings({ filenameQuery: nextFilenameQuery }),
    [updateSettings]
  )
  const setSizeFilter = useCallback(
    (size: BackgroundsSizeFilter) => updateSettings({ size }),
    [updateSettings]
  )
  const setPlacedFilter = useCallback(
    (placed: BackgroundsPlacedFilter) => updateSettings({ placed }),
    [updateSettings]
  )
  const setShowFilename = useCallback(
    (nextShowFilename: boolean) => updateSettings({ showFilename: nextShowFilename }),
    [updateSettings]
  )
  const setFiltersOpen = useCallback(
    (nextFiltersOpen: boolean) => updateSettings({ filtersOpen: nextFiltersOpen }),
    [updateSettings]
  )
  const setSort = useCallback(
    (nextSort: BackgroundsPanelSettings['sort']) => updateSettings({ sort: nextSort }),
    [updateSettings]
  )

  const handleClearFilters = useCallback(() => {
    updateSettings(CLEARED_FILTERS)
  }, [updateSettings])

  // Formula colors are plain swatches with no CMS keywords, so the keyword picker is hidden for
  // that type, and switching to it starts from a clean slate — every applied filter is cleared.
  const handleTypeChange = useCallback(
    (next: BackgroundsTypeOption) => {
      updateSettings(
        next === 'Formula colors' ? { type: next, ...CLEARED_FILTERS } : { type: next }
      )
    },
    [updateSettings]
  )

  return {
    settings,
    setSelectedKeywordIds,
    setFilenameQuery,
    setSizeFilter,
    setPlacedFilter,
    setShowFilename,
    setFiltersOpen,
    setSort,
    handleClearFilters,
    handleTypeChange,
  }
}
